import GeneralLineDiagramStore from '../GeneralLineDiagramStore';
import {AnalyticsTab, DiagramMode} from '../store';
import {KPI} from '../../kpi';
import {transaction} from 'mobx';
import {
    ChannelTimeSeriesRequest as CampaignChannelTimeSeriesRequest,
    getDailyCampaignData,
    getDailyCampaignDataSummary,
    getHourlyCampaignData,
    getHourlyCampaignDataSummary,
    TimeSeriesRequest as CampaignTimeSeriesRequest,
} from '../../apis/CampaignAPI';
import {AxiosResponse} from 'axios';
import {TimeSeriesEntry} from '../../apis/TimeSeriesEntry';

class CampaignLineDiagramStore extends GeneralLineDiagramStore {
    public dependantAnalyticsTab: AnalyticsTab = AnalyticsTab.CampaignView;

    public getChartApi(): (param: CampaignTimeSeriesRequest) => Promise<AxiosResponse<TimeSeriesEntry[]>> {
        const {diagramMode} = this.mainStore;

        switch (diagramMode) {
            case DiagramMode.TwentyFourHourMode:
            case DiagramMode.Hourly:
                return getHourlyCampaignData;
            case DiagramMode.Daily:
                return getDailyCampaignData;
            default:
                throw Error('API for not available for ' + diagramMode);
        }
    }

    public getSummaryChartApi(): (param: CampaignChannelTimeSeriesRequest) => Promise<AxiosResponse<TimeSeriesEntry[]>> {
        const {diagramMode} = this.mainStore;
        switch (diagramMode) {
            case DiagramMode.TwentyFourHourMode:
            case DiagramMode.Hourly:
                return getHourlyCampaignDataSummary;
            case DiagramMode.Daily:
                return getDailyCampaignDataSummary;
            default:
                throw Error('API for not available for ' + diagramMode);
        }
    }

    public addTermToDiagram(campaignId: string, kpi: KPI, campaignName: string, channelName: string, colorIndex?: number) {
        /**
         * Adds a term to the diagram. The term will be requested from the backend and loaded into the diagram when present.
         * The currently selected channel will be chosen as the channel to this term.
         * @type {boolean}
         */
        const duplicateFound = this.requestedNormalDiagrams.some((data) => {
            return data.searchtermOrProductId === campaignId && data.channelName === channelName && data.kpi === kpi;
        });

        if (!duplicateFound) {
            this.requestedNormalDiagrams.push({
                title: campaignName,
                channelName,
                kpi,
                searchtermOrProductId: campaignId,
            });
            this.updateTermColorIndexes(campaignId, kpi, channelName, colorIndex);
            this.loadDiagramData(campaignId, channelName, kpi);
        }
    }

    public addChannelToDiagram(channelName: string, kpi: KPI, title?: string, colorIndex?: number, testId?: string, variantId?: string) {
        /**
         * Add a channel to the diagram. The channel data gets requested from the backend.
         * @type {boolean}
         */
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        const duplicateFound = this.requestedChannelDiagrams.some((data) => {
            return data.channelName === channelName && data.kpi === kpi;
        });

        if (!duplicateFound) {
            this.requestedChannelDiagrams.push({
                channelName,
                kpi,
                title: title ? title : undefined,
            });
            this.updateTermColorIndexes(channelName, kpi, selectedChannel.name, colorIndex);
            this.loadDiagramDataForChannel(channelName, kpi);
        }
    }

    public loadDiagramData(campaignId: string, channelName: string, kpi: KPI) {
        this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());
        const fullChannel = this.channelState.getFullChannelForChannelName(channelName);
        const {mainDateRange, compareDateRange} = this.timeState;

        this.getChartApi()({
            channel: channelName,
            kpi,
            campaignId,
            interval: mainDateRange,
        })
            .then((resp) => {
                transaction(() => {
                    this.addToLoadedDiagramData({
                        channel: fullChannel,
                        searchtermOrProductId: campaignId,
                        kpi,
                        isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                        isChannelDiagram: false,
                        isCompareDiagram: false,
                    });
                    this.addToDiagram(
                        resp.data,
                        {channel: fullChannel, searchtermOrProductId: campaignId, kpi, isCompareDiagram: false, isChannelDiagram: false},
                        mainDateRange
                    );
                    this.updateDiagramView();
                });
            })
            .catch(() => {
                /*no op*/
            });

        if (compareDateRange.startDate !== null && compareDateRange.endDate !== null && this.timeState.allowDateCompareRange) {
            // request the second data set with the comparison time range
            this.getChartApi()({
                channel: channelName,
                kpi,
                campaignId,
                interval: compareDateRange,
            })
                .then((resp) => {
                    transaction(() => {
                        this.addToLoadedDiagramData({
                            channel: fullChannel,
                            searchtermOrProductId: campaignId,
                            kpi,
                            isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                            isChannelDiagram: false,
                            isCompareDiagram: true,
                        });
                        this.addToDiagram(
                            resp.data,
                            {
                                channel: fullChannel,
                                searchtermOrProductId: campaignId,
                                kpi,
                                isCompareDiagram: true,
                                isChannelDiagram: false,
                            },
                            mainDateRange
                        );
                        this.updateDiagramView();
                    });
                })
                .catch(() => {
                    /*no op*/
                });
        }
    }

    public loadDiagramDataForChannel(channelName: string, kpi: KPI) {
        this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());
        const fullChannel = this.channelState.getFullChannelForChannelName(channelName);
        const {mainDateRange, compareDateRange} = this.timeState;

        this.getSummaryChartApi()({
            channel: channelName,
            kpi,
            interval: this.getDateRangeConversion()(mainDateRange),
        })
            .then((resp) => {
                transaction(() => {
                    this.addToLoadedDiagramData({
                        channel: fullChannel,
                        kpi,
                        searchtermOrProductId: channelName,
                        isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                        isChannelDiagram: true,
                        isCompareDiagram: false,
                    });
                    this.addToDiagram(
                        resp.data,
                        {channel: fullChannel, searchtermOrProductId: channelName, kpi, isCompareDiagram: false, isChannelDiagram: true},
                        mainDateRange
                    );
                    this.updateDiagramView();
                });
            })
            .catch(() => {
                /*no op*/
            });

        if (compareDateRange.startDate !== null && compareDateRange.endDate !== null && this.timeState.allowDateCompareRange) {
            // request the second data set with the comparison time range
            this.getSummaryChartApi()({
                channel: channelName,
                kpi,
                interval: this.getDateRangeConversion()(compareDateRange),
            })
                .then((resp) => {
                    transaction(() => {
                        this.addToLoadedDiagramData({
                            channel: fullChannel,
                            kpi,
                            isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                            searchtermOrProductId: channelName,
                            isChannelDiagram: true,
                            isCompareDiagram: true,
                        });
                        this.addToDiagram(
                            resp.data,
                            {channel: fullChannel, searchtermOrProductId: channelName, kpi, isCompareDiagram: true, isChannelDiagram: false},
                            mainDateRange
                        );
                        this.updateDiagramView();
                    });
                })
                .catch(() => {
                    /*no op*/
                });
        }
    }

    protected reloadDiagramData() {
        this.requestedNormalDiagrams.forEach((data) => {
            this.loadDiagramData(data.searchtermOrProductId, data.channelName, data.kpi);
        });

        this.requestedChannelDiagrams.forEach((data) => {
            this.loadDiagramDataForChannel(data.channelName, data.kpi);
        });
    }
}

export default CampaignLineDiagramStore;



// WEBPACK FOOTER //
// ./src/stores/campaign/CampaignLineDiagramStore.ts